/**
 * UGC Carousel Custom Element
 *
 * Features:
 * - CSS Grid with 2-row layout and edge-to-edge horizontal scrolling
 * - Media tiles (image, video, video URL) with hover / tap / autoplay modes
 * - Testimonial tiles with configurable row/column spans
 * - Responsive column calculation based on viewport
 * - Progress bar and navigation arrows with fades
 */

/**
 * Debounce function to limit how often a function can fire
 * @param {Function} fn - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} The debounced function
 */
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

if (!customElements.get('ugc-carousel')) {
  customElements.define(
    'ugc-carousel',
    class UgcCarousel extends HTMLElement {
      constructor() {
        super();
        
        /* Cache DOM element references */
        this.grid = this.querySelector('.ugc-carousel__grid');
        this.prevBtn = this.querySelector('.ugc-carousel__nav--prev');
        this.nextBtn = this.querySelector('.ugc-carousel__nav--next');
        this.fadePrev = this.querySelector('.ugc-carousel__fade--left');
        this.fadeNext = this.querySelector('.ugc-carousel__fade--right');
      }

      connectedCallback() {
        if (!this.grid) return;

        this.initNavigation();
        this.initPagination();
        this.initVideoPlayback();
        this.initModalVideos();
        this.updateColumnWidth();
        this.updateNavState();
        this.updateRowHeight();

        // Store handler reference for cleanup
        this.resizeHandler = debounce(() => {
          this.updateColumnWidth();
          this.updateRowHeight();
        }, 150);
        
        window.addEventListener('resize', this.resizeHandler);

        // Store observer reference for cleanup
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(debounce(() => {
            this.updateColumnWidth();
            this.updateRowHeight();
          }, 150));
          this.resizeObserver.observe(this.grid);
        }
      }

      disconnectedCallback() {
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }
        
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }

      /** Initialise prev/next arrow click handlers and scroll listener */
      initNavigation() {
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.scroll('prev'));
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.scroll('next'));
        }
        
        this.grid.addEventListener('scroll', () => {
          this.updateNavState();
          this.updateRowHeight();
        });
      }

      /** Scroll grid by one column width, with smart mobile centering */
      scroll(direction) {
        const items = Array.from(
          this.grid.querySelectorAll('.ugc-carousel__item')
        );
        if (!items.length) return;

        const isMobile = window.matchMedia('(max-width: 499px)').matches;

        // Desktop: keep simple "one column" scroll
        if (!isMobile) {
          const firstItem = items[0];
          const gap = parseFloat(getComputedStyle(this.grid).gap) || 0;
          const columnWidth = firstItem.offsetWidth + gap;
          const scrollAmount = direction === 'next' ? columnWidth : -columnWidth;

          this.grid.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          return;
        }

        // Mobile: center the target slide in the viewport (except first slide)
        const { scrollLeft, clientWidth, scrollWidth } = this.grid;
        const gridStyles = getComputedStyle(this.grid);
        const paddingLeft = parseFloat(gridStyles.paddingLeft) || 0;
        const viewportCenter = scrollLeft + clientWidth / 2 - paddingLeft;

        // Find the item whose center is currently closest to the viewport center
        let currentIndex = 0;
        let smallestDelta = Infinity;

        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const delta = Math.abs(itemCenter - viewportCenter);
          if (delta < smallestDelta) {
            smallestDelta = delta;
            currentIndex = index;
          }
        });

        const targetIndex =
          direction === 'next'
            ? Math.min(currentIndex + 1, items.length - 1)
            : Math.max(currentIndex - 1, 0);

        const targetItem = items[targetIndex];
        if (!targetItem) return;

        let targetScrollLeft;

        if (targetIndex === 0) {
          // First slide: align to the start (respecting left padding)
          targetScrollLeft = targetItem.offsetLeft - paddingLeft;
        } else {
          // Other slides: center within the visible content area
          const targetCenter = targetItem.offsetLeft + targetItem.offsetWidth / 2;
          // We want targetCenter == scrollLeft + clientWidth / 2 - paddingLeft
          targetScrollLeft = targetCenter - (clientWidth / 2 - paddingLeft);
        }

        // Clamp within scrollable bounds
        const maxScroll = scrollWidth - clientWidth;
        targetScrollLeft = Math.max(0, Math.min(maxScroll, targetScrollLeft));

        this.grid.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }

      /** Update arrow disabled states and fade visibility based on scroll position */
      updateNavState() {
        if (!this.grid) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.grid;

        const canScrollPrev = scrollLeft > 0;
        const canScrollNext = scrollLeft + clientWidth < scrollWidth - 1;

        if (this.prevBtn) {
          this.prevBtn.disabled = !canScrollPrev;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = !canScrollNext;
        }

        // Show/hide gradient fades based on scrollability, same logic as arrows
        if (this.fadePrev) {
          this.fadePrev.style.opacity = canScrollPrev ? '1' : '0';
        }

        if (this.fadeNext) {
          this.fadeNext.style.opacity = canScrollNext ? '1' : '0';
        }

        this.updatePagination();
      }

      /** Update progress bar width based on scroll position */
      updatePagination() {
        if (!this.progressBar || !this.grid) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.grid;
        const maxScroll = scrollWidth - clientWidth;
        const rawProgress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;

        // Keep a small visible portion of the bar even at the start
        const minProgress = 10; // percent
        const progress = rawProgress > 0 ? Math.max(rawProgress, minProgress) : minProgress;

        this.progressBar.style.width = `${progress}%`;
      }

      /** Initialise progress bar element */
      initPagination() {
        const pagination = this.querySelector('.ugc-carousel__pagination');
        if (!pagination) return;

        // Ensure we have the expected progress bar structure
        let progressBar = pagination.querySelector('.ugc-carousel__progress-bar');
        if (!progressBar) {
          pagination.innerHTML =
            '<div class="ugc-carousel__progress"><div class="ugc-carousel__progress-bar"></div></div>';
          progressBar = pagination.querySelector('.ugc-carousel__progress-bar');
        }

        this.progressBar = progressBar;
      }

      /** Calculate row height to maintain ~16:9 aspect ratio for 2-row tiles */
      updateRowHeight() {
        if (!this.grid) return;

        // Use the calculated column width, not the first item's width
        // (first item might span multiple columns)
        const styles = getComputedStyle(this.grid);
        const columnWidth =
          parseFloat(styles.getPropertyValue('--column-width')) || 280;
        const gap = parseFloat(styles.gap) || 12;

        // Row height for 2-row tiles to achieve ~16:9 portrait ratio
        // Media height = 2 * rowHeight + gap
        // Target: mediaHeight / columnWidth = 16/9
        const targetMediaRatio = 16 / 9;
        const mediaHeight = targetMediaRatio * columnWidth;
        const rowHeight = Math.max(0, (mediaHeight - gap) / 2);

        this.grid.style.setProperty('--row-height', `${rowHeight}px`);
      }

      /** Calculate column width based on viewport and columns-visible settings */
      updateColumnWidth() {
        const section = this.closest('.section');
        if (!section) return;

        // Get section width (this is page-width constrained)
        const wrapper = section.querySelector('.section__wrapper');
        const sectionWidth = wrapper?.clientWidth || section.clientWidth;

        // Check breakpoints:
        // - mobile: < 500px  -> 1-ish column (setting-driven)
        // - small:  500–899  -> 2 columns
        // - medium: 900–1199 -> 3 columns
        const isMobile = window.matchMedia('(max-width: 499px)').matches;
        const isTwoColBand =
          !isMobile && window.matchMedia('(max-width: 899px)').matches;
        const isThreeColBand =
          !isMobile &&
          !isTwoColBand &&
          window.matchMedia('(max-width: 1199px)').matches;

        // Get columns visible from CSS variable / hardcoded breakpoints
        const styles = getComputedStyle(section);
        let columnsVisible;

        if (isMobile) {
          columnsVisible =
            parseFloat(styles.getPropertyValue('--columns-visible-mobile')) || 1.2;
        } else if (isTwoColBand) {
          columnsVisible = 2;
        } else if (isThreeColBand) {
          columnsVisible = 3;
        } else {
          columnsVisible =
            parseFloat(styles.getPropertyValue('--columns-visible')) || 4;
        }

        // Get gap
        const gap = parseFloat(getComputedStyle(this.grid).gap) || 12;

        // Calculate: (available width - gaps) / columns
        const totalGaps = (Math.ceil(columnsVisible) - 1) * gap;
        const columnWidth = (sectionWidth - totalGaps) / columnsVisible;

        this.grid.style.setProperty('--column-width', `${columnWidth}px`);
      }

      /** Initialise video hover / tap / autoplay behaviour based on device capabilities */
      initVideoPlayback() {
        const videoWraps = this.querySelectorAll('.ugc-tile__video-wrap');
        // Treat devices with a real hover-capable pointer as "desktop" for hover-to-play
        const isDesktopHover = window.matchMedia(
          '(hover: hover) and (pointer: fine)'
        ).matches;

        videoWraps.forEach((wrap) => {
          const video = wrap.querySelector('video');
          if (!video) return;

          const desktopMode = wrap.dataset.playMode || 'hover';
          const mobileMode = wrap.dataset.playModeMobile || 'autoplay';
          const currentMode = isDesktopHover ? desktopMode : mobileMode;

          // Set active mode as data attribute for CSS
          wrap.dataset.activeMode = currentMode;

          if (currentMode === 'autoplay') {
            // Autoplay - start playing immediately
            video.play().catch(() => {
              // Autoplay may be blocked, fail silently
            });
          } else if (currentMode === 'hover') {
            // Desktop hover - play on mouseenter
            wrap.addEventListener('mouseenter', () => {
              video.play().catch(() => {});
            });

            wrap.addEventListener('mouseleave', () => {
              video.pause();
              video.currentTime = 0;
            });
          } else if (currentMode === 'tap') {
            // Mobile tap - toggle play/pause on click
            wrap.addEventListener('click', (e) => {
              // Don't trigger if clicking a link inside
              if (e.target.closest('a')) return;

              if (video.paused) {
                video.play().catch(() => {});
                wrap.dataset.playing = 'true';
              } else {
                video.pause();
                wrap.dataset.playing = 'false';
              }
            });
          }
        });
      }

      /** Handle modal video autoplay when modal opens */
      initModalVideos() {
        // Listen for modal open events
        document.addEventListener('modal:handleAfterShow', (e) => {
          const modal = e.target;
          if (!modal.classList.contains('ugc-modal')) return;
          
          const video = modal.querySelector('.ugc-modal__video[data-autoplay="true"]');
          if (video) {
            video.currentTime = 0;
            video.play().catch(() => {
              // Autoplay blocked, user can use controls
            });
          }
          
          // Reinitialise Trustpilot widget if present
          if (modal.classList.contains('ugc-modal--trustpilot')) {
            const trustpilotWidget = modal.querySelector('.trustpilot-widget');
            if (trustpilotWidget && window.Trustpilot) {
              try {
                window.Trustpilot.loadFromElement(trustpilotWidget, true);
              } catch (error) {
                // Third-party widget failed to load, fail gracefully
                console.debug('Trustpilot widget initialization failed:', error.message);
              }
            }
          }
        });
        
        // Pause video when modal closes
        document.addEventListener('modal:handleAfterHide', (e) => {
          const modal = e.target;
          if (!modal.classList.contains('ugc-modal')) return;
          
          const video = modal.querySelector('.ugc-modal__video');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        });
      }
    }
  );
}
